import { useState } from 'react';
import { FolderPlus, Plus, Target, ChevronRight } from 'lucide-react';
import ProjectNode from './ProjectNode';
import TaskNode from './TaskNode';

export default function GoalHierarchy({
  goal,
  projects,
  directTasks,
  projectTasksMap,
  onAddProject,
  onAddTaskToProject,
  onAddTaskToGoal,
  onCompleteTask,
  onUncompleteTask,
  isAdmin
}) {
  const [showDirectTasks, setShowDirectTasks] = useState(true);

  const activeDirectTasks = directTasks.filter(t => !t.completedAt);
  const completedDirectTasks = directTasks.filter(t => t.completedAt);
  const hasDirectTasks = directTasks.length > 0;
  const hasProjects = projects.length > 0;

  return (
    <div className="mb-6">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Target size={16} className="text-accent" />
          Projects & Tasks
        </h3>

        {isAdmin && (
          <div className="flex gap-1">
            <button
              onClick={onAddTaskToGoal}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-accent
                         bg-accent/10 hover:bg-accent/20 rounded-md transition-colors"
            >
              <Plus size={12} />
              Task
            </button>
            <button
              onClick={onAddProject}
              className="flex items-center gap-1 px-2 py-1 text-[10px] text-purple-500
                         bg-purple-500/10 hover:bg-purple-500/20 rounded-md transition-colors"
            >
              <FolderPlus size={12} />
              Project
            </button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {!hasProjects && !hasDirectTasks && (
        <div className="text-center py-8 bg-surface-elevated rounded-xl border border-dashed border-border">
          <Target size={24} className="mx-auto text-text-muted mb-2" />
          <p className="text-sm text-text-secondary">No projects or tasks yet</p>
          <p className="text-[10px] text-text-tertiary mt-1">
            Add projects to organize work, or add tasks directly
          </p>
        </div>
      )}

      {/* Projects */}
      {hasProjects && (
        <div className="space-y-1">
          {projects.map(project => (
            <ProjectNode
              key={project.id}
              project={project}
              tasks={projectTasksMap[project.id] || []}
              onAddTask={onAddTaskToProject}
              onCompleteTask={onCompleteTask}
              onUncompleteTask={onUncompleteTask}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {/* Direct tasks (not in a project) */}
      {hasDirectTasks && (
        <div className="mt-3">
          <div
            onClick={() => setShowDirectTasks(!showDirectTasks)}
            className="flex items-center gap-2 p-2 rounded-lg bg-surface-elevated border border-border/50
                       cursor-pointer hover:bg-surface-hover transition-colors"
          >
            <ChevronRight
              size={14}
              className={`text-text-muted flex-shrink-0 transition-transform ${
                showDirectTasks ? 'rotate-90' : ''
              }`}
            />
            <Target size={14} className="text-accent flex-shrink-0" />
            <span className="text-[12px] text-text-primary font-medium flex-1">
              Direct Tasks
            </span>
            <span className="text-[10px] text-text-tertiary">
              {directTasks.filter(t => t.completedAt).length}/{directTasks.length}
            </span>

            {isAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddTaskToGoal();
                }}
                className="p-1 rounded-md bg-transparent border-none text-accent cursor-pointer
                           hover:bg-accent-muted transition-all"
                title="Add task"
              >
                <Plus size={12} />
              </button>
            )}
          </div>

          {showDirectTasks && (
            <div className="ml-6 mt-1 pl-2 border-l-2 border-border/30">
              {/* Active tasks */}
              {activeDirectTasks.map(task => (
                <TaskNode
                  key={task.id}
                  task={task}
                  onComplete={onCompleteTask}
                  onUncomplete={onUncompleteTask}
                />
              ))}

              {/* Separator if both active and completed */}
              {activeDirectTasks.length > 0 && completedDirectTasks.length > 0 && (
                <div className="border-t border-border/30 my-1" />
              )}

              {/* Completed tasks (show first 3) */}
              {completedDirectTasks.slice(0, 3).map(task => (
                <TaskNode
                  key={task.id}
                  task={task}
                  onComplete={onCompleteTask}
                  onUncomplete={onUncompleteTask}
                />
              ))}

              {/* More completed indicator */}
              {completedDirectTasks.length > 3 && (
                <p className="text-[10px] text-text-muted py-1">
                  +{completedDirectTasks.length - 3} more completed
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
